import { Fragment, useEffect, useMemo } from 'react';
import { useRef, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';

import { cloneDeep } from 'lodash';
import { observer } from 'mobx-react-lite';

import { Breadcrumb } from '@components/breadcrumb';
import { Button } from '@components/button';
import { Stack } from '@components/stack';
import { useScopedTranslate } from '@locale/translate';

import { useCreateTasklist, useUpdateTasklist } from 'api/templates/hooks';
import { useTasklistTemplates } from 'api/templates/requests';
import { Header } from 'components/headers';
import { Loading } from 'components/Loading';
import { PageContent } from 'components/Page';
import { useStore } from 'models/Provider';
import { EditingTasklist, TasklistTemplate } from 'types/templates';

import { TasklistForm } from './tasklist-form';

export const EditTasklistTemplate = observer(function EditTasklistTemplate() {
  const { templateID } = useParams();
  const { t } = useScopedTranslate(locale);
  const listQuery = useTasklistTemplates();

  const updateTasklist = useUpdateTasklist({
    onSuccess: (data) => {
      initialData.current = data;
      setEditing(data);
    },
  });

  const createTasklist = useCreateTasklist({
    onSuccess: (data) => {
      navigate(`/settings/templates/tasks/${data.id}`, { replace: true });
    },
  });

  const [selectedTemplate, setSelectedTemplate] = useState<TasklistTemplate | undefined>({
    name: '',
    items: [],
    id: -1,
  } as TasklistTemplate);

  useEffect(() => {
    if (listQuery.data && templateID !== 'new') {
      const template = listQuery.data.find((t) => t.id === Number(templateID));
      setSelectedTemplate(template);
      if (template) {
        initialData.current = cloneDeep(template);
        setEditing(initialData.current);
      }
    }
  }, [listQuery.data, templateID]);

  const navigate = useNavigate();

  const initialData = useRef<EditingTasklist>(
    selectedTemplate
      ? cloneDeep(selectedTemplate)
      : ({ name: '', items: [], id: -1 } as TasklistTemplate),
  );
  const [draft, setEditing] = useState<EditingTasklist>(initialData.current);

  const isDirty = useMemo<boolean>(() => {
    if (draft.name === '') return false;
    if (draft.name !== initialData.current.name) return true;
    if (draft.items.length !== initialData.current.items.length) return true;
    if (draft.items.some((item) => item.isNew)) return true;

    return draft.items.some((item, index) => initialData.current.items[index].text !== item.text);
  }, [draft]);

  function cancel() {
    if (draft.id === -1) {
      navigate('/settings/templates/tasks');
      return;
    }
    setEditing(initialData.current);
  }

  async function save() {
    // The API doesn't like getting IDs in there so we need to remove them from new tasks.
    if (draft.id === -1) {
      createTasklist.mutate({
        name: draft.name,
        items: draft.items
          .filter((item) => item.text && item.text !== '')
          .map((item) => ({
            description: item.description,
            order: item.order,
            text: item.text,
          })),
      });
    } else {
      updateTasklist.mutate({
        ...draft,
        items: draft.items
          .filter((item) => item.text && item.text !== '')
          .map((item) => {
            if (item.isNew) {
              return {
                description: item.description,
                order: item.order,
                text: item.text,
              };
            }
            return item;
          }),
      });
    }
  }

  if (listQuery.isLoading) return <Loading delay={500} />;
  if (!listQuery.data || !selectedTemplate) return <Navigate to="/settings/templates/tasks" />;
  return (
    <Fragment>
      <Header
        breadcrumbs={<Crumbs template={selectedTemplate} />}
        actions={
          <Stack space={2} orientation="horizontal">
            {isDirty ? (
              <Button onClick={cancel} variant="secondary" text={t('cancel')} />
            ) : (
              <Link to="/settings/templates/tasks">
                <Button variant="secondary" text={t('back')} />
              </Link>
            )}
            <Button
              isDisabled={createTasklist.isLoading || updateTasklist.isLoading || !isDirty}
              onClick={() => save()}
              text={t('save')}
              variant="primary-blue"
            />
          </Stack>
        }
      />

      <PageContent>
        <Stack justify="center">
          <TasklistForm
            selected={selectedTemplate}
            onSave={() => save()}
            setEditing={setEditing}
            editing={draft}
          />
        </Stack>
      </PageContent>
    </Fragment>
  );
});

const locale = {
  en: {
    cancel: 'Cancel',
    back: 'Back',
    save: 'Save',
  },
  is: {
    cancel: 'Hætta við',
    back: 'Til baka',
    save: 'Vista',
  },
};

const Crumbs = observer(({ template }: { template: TasklistTemplate }) => {
  const { t } = useStore();

  let items = [
    { label: t('company.templates.crumbs.root'), link: '/settings/templates' },
    { label: t('company.templates.tabs.tasks'), link: '/settings/templates/tasks' },
  ];

  if (template.name) {
    items.push({ label: template.name || template.name, link: '' });
  } else {
    items.push({ label: t('company.templates.crumbs.new'), link: '' });
  }

  return <Breadcrumb items={items} />;
});
